import { useEffect, useRef, useState } from 'react';

/**
 * Returns a ref to attach to an element + a `revealed` boolean
 * that flips to true the first time the element scrolls into view.
 * Used to drive subtle fade-up animations site-wide.
 */
export default function useReveal({ rootMargin = '0px 0px -10% 0px', threshold = 0.12 } = {}) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') { setRevealed(true); return; }

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) { setRevealed(true); io.disconnect(); break; }
        }
      },
      { rootMargin, threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin, threshold]);

  return { ref, revealed };
}

export function Reveal({ children, delay = 0, as: Tag = 'div', className = '', ...props }) {
  const { ref, revealed } = useReveal();
  const style = {
    transition: 'opacity 700ms ease-out, transform 700ms ease-out',
    transitionDelay: `${delay}ms`,
    opacity: revealed ? 1 : 0,
    transform: revealed ? 'translateY(0)' : 'translateY(20px)',
    willChange: 'opacity, transform',
  };
  return (
    <Tag ref={ref} style={style} className={className} {...props}>
      {children}
    </Tag>
  );
}
